import { useMemo, useState } from 'react'
import { BookOpen, Lock, RotateCcw, Search, Wrench } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { Badge, EmptyState } from '@/components/ui/primitives'
import { GUIDE, REVERSIBLE_ACTIONS, type Action, type Section, type Undo } from '@/pages/help/guide'
import { t } from '@/i18n'

/**
 * How the shop works, and how to take back anything it recorded.
 *
 * Written as one long page rather than a help centre with articles: a cashier looking for
 * "how do I undo this" needs to find it with one search box and no navigating, usually while
 * somebody is waiting at the counter.
 */
export function HelpPage() {
  const [query, setQuery] = useState('')

  const sections = useMemo(() => filter(GUIDE, query), [query])
  const hits = sections.reduce((count, section) => count + section.actions.length, 0)

  return (
    <div className="pb-page mx-auto w-full max-w-4xl px-4 py-6 sm:px-6">
      <header className="mb-5">
        <h1 className="text-xl font-semibold">{t('How everything works')}</h1>
        <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
          {t('Every action in the shop, who can do it, and how to take it back.')}
        </p>
      </header>

      <section className="surface-raised mb-6 rounded-[var(--radius-card)] border p-4">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <RotateCcw className="h-4 w-4" />
          {t('What “reversing” means here')}
        </h2>
        <p className="mt-2 text-sm text-[var(--text-secondary)]">
          {t('Nothing is ever deleted. Reversing an entry keeps the original exactly as it was recorded, marks it with who undid it and why, and puts the money or stock back with a second entry. That is deliberate: once a row can quietly disappear, an honest mistake and a theft leave the same trace — none at all.')}
        </p>
        <p className="mt-2 text-sm text-[var(--text-secondary)]">
          {t('A reversed entry stops counting towards any total the moment it is reversed, but stays on the screen it was recorded on, struck through. Every reversal asks for a reason, and the reason is kept.')}
        </p>
      </section>

      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={t('Search — try “refund”, “safe”, “undo a delivery”')}
        leading={<Search className="h-4 w-4" />}
        wrapperClassName="mb-5"
        autoComplete="off"
      />

      {query.trim().length > 0 && (
        <p className="mb-4 text-sm text-[var(--text-secondary)]">
          {hits} {hits === 1 ? 'result' : 'results'}
        </p>
      )}

      {query.trim().length === 0 && (
        <>
          <ReversalSummary />
          <ContentsNav />
        </>
      )}

      {sections.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title={t('Nothing matched')}
          description={t('Try a shorter word — “safe”, “stock”, “VAT”, “password”.')}
        />
      ) : (
        <div className="flex flex-col gap-8">
          {sections.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-20">
              <h2 className="text-lg font-semibold">{t(section.title)}</h2>
              <p className="mt-1 mb-3 text-sm text-[var(--text-secondary)]">{t(section.blurb)}</p>

              <ul className="flex flex-col gap-3">
                {section.actions.map((action) => (
                  <ActionCard key={action.name} action={action} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

/** The short answer to "what can I undo", before any of the detail. */
function ReversalSummary() {
  return (
    <section className="mb-6">
      <h2 className="mb-2 text-sm font-semibold">{t('What an admin can undo')}</h2>
      <ul className="surface-raised divide-y divide-[var(--border-subtle)] rounded-[var(--radius-card)] border">
        {REVERSIBLE_ACTIONS.map(({ section, action }) => (
          <li key={action.name} className="flex flex-wrap items-baseline gap-x-2 gap-y-1 p-3 text-sm">
            <span className="font-medium">{t(action.name)}</span>
            <span className="text-xs text-[var(--text-secondary)]">{t(section)}</span>
            <span className="ms-auto text-xs text-[var(--text-secondary)]">
              {action.undo.kind === 'reversible' ? t(action.undo.where) : ''}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-[var(--text-secondary)]">
        {t('Reversing needs the reverse-transactions right, which only the owner holds by default. Managers do not get it unless you grant it: whoever holds it can cancel the record of their own mistakes.')}
      </p>
    </section>
  )
}

function ContentsNav() {
  return (
    <nav className="mb-6 flex flex-wrap gap-2">
      {GUIDE.map((section) => (
        <a
          key={section.id}
          href={`#${section.id}`}
          className="surface-raised rounded-lg border px-3 py-1.5 text-sm hover:bg-[var(--surface-sunken)]"
        >
          {t(section.title)}
        </a>
      ))}
    </nav>
  )
}

function ActionCard({ action }: { action: Action }) {
  return (
    <li className="surface-raised rounded-[var(--radius-card)] border p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-medium">{t(action.name)}</h3>
        <UndoBadge undo={action.undo} />
      </div>

      <p className="mt-1 text-sm text-[var(--text-secondary)]">{t(action.what)}</p>

      <dl className="mt-3 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-[auto_1fr]">
        <dt className="text-[var(--text-secondary)]">{t('Who')}</dt>
        <dd>{t(action.who)}</dd>
        <dt className="text-[var(--text-secondary)]">{t('Where')}</dt>
        <dd>{t(action.where)}</dd>
      </dl>

      <UndoDetail undo={action.undo} />
    </li>
  )
}

function UndoBadge({ undo }: { undo: Undo }) {
  if (undo.kind === 'reversible') return <Badge tone="success">{t('Can be reversed')}</Badge>
  if (undo.kind === 'corrected') return <Badge tone="neutral">{t('Corrected another way')}</Badge>
  return <Badge tone="warn">{t('Cannot be undone')}</Badge>
}

function UndoDetail({ undo }: { undo: Undo }) {
  if (undo.kind === 'reversible') {
    return (
      <div className="surface-sunken mt-3 rounded-lg p-3 text-xs">
        <p className="flex items-center gap-1.5 font-medium">
          <RotateCcw className="h-3.5 w-3.5" />
          {t('To undo it')}
        </p>
        <p className="mt-1 text-[var(--text-secondary)]">{t(undo.where)}</p>
        <p className="mt-1 text-[var(--text-secondary)]">{t(undo.effect)}</p>
        {undo.refusedWhen && (
          <p className="mt-1.5 text-warn-500">{t('Refused when:')} {t(undo.refusedWhen)}</p>
        )}
      </div>
    )
  }

  if (undo.kind === 'corrected') {
    return (
      <div className="surface-sunken mt-3 rounded-lg p-3 text-xs">
        <p className="flex items-center gap-1.5 font-medium">
          <Wrench className="h-3.5 w-3.5" />
          {t('To correct it')}
        </p>
        <p className="mt-1 text-[var(--text-secondary)]">{t(undo.how)}</p>
      </div>
    )
  }

  return (
    <div className="mt-3 rounded-lg bg-warn-500/10 p-3 text-xs">
      <p className="flex items-center gap-1.5 font-medium text-warn-500">
        <Lock className="h-3.5 w-3.5" />
        {t('This one is permanent')}
      </p>
      <p className="mt-1 text-[var(--text-secondary)]">{t(undo.why)}</p>
    </div>
  )
}

/** Matches on everything an entry says, so searching "sold again" finds the refusal note. */
function filter(guide: Section[], query: string): Section[] {
  const needle = query.trim().toLowerCase()
  if (needle.length === 0) return guide

  return guide
    .map((section) => ({
      ...section,
      actions: section.actions.filter((action) => haystack(action, section).includes(needle)),
    }))
    .filter((section) => section.actions.length > 0)
}

function haystack(action: Action, section: Section): string {
  const undo =
    action.undo.kind === 'reversible'
      ? `${action.undo.where} ${action.undo.effect} ${action.undo.refusedWhen ?? ''} reverse undo`
      : action.undo.kind === 'corrected'
        ? `${action.undo.how} correct fix`
        : `${action.undo.why} permanent cannot undo`

  // Both languages, so an Arabic reader searching in Arabic finds the entry whose heading they
  // can actually see, and searching in English still works for anything only written in English —
  // which is most of the detail, since only the headings and the short fields are translated.
  const english = `${section.title} ${action.name} ${action.what} ${action.who} ${action.where} ${undo}`
  const translated = `${t(section.title)} ${t(action.name)} ${t(action.what)} ${t(action.who)} ${t(action.where)}`

  return `${english} ${translated}`.toLowerCase()
}
